import api from './client';

// The notes controller wraps responses as `{ success, data, ... }`.
// This helper transparently unwraps that envelope while still supporting
// payloads returned directly.
const unwrap = <T>(payload: any): T => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    'data' in payload
  ) {
    return payload.data as T;
  }
  return payload as T;
};

// Enums
export enum NoteCategory {
  GENERAL = 'general',
  BEHAVIOR = 'behavior',
  HEALTH = 'health',
  BREEDING = 'breeding',
  FEEDING = 'feeding',
  TRAINING = 'training',
  VETERINARY = 'veterinary',
  REMINDER = 'reminder',
  OBSERVATION = 'observation',
  OTHER = 'other',
}

export enum NotePriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

// Interfaces
export interface NoteMetadata {
  reminder?: {
    isActive: boolean;
    reminderDate?: Date;
    reminderTime?: string;
    recurring?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  };
  attachments?: string[];
  location?: string;
  weather?: string;
}

export interface Note {
  id: string;
  title: string;
  content: string;
  category: NoteCategory;
  priority: NotePriority;
  birdId?: string;
  speciesId?: string;
  tags?: string[];
  metadata?: NoteMetadata;
  isPinned: boolean;
  isArchived: boolean;
  reminderDate?: string;
  bird?: any;
  species?: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNoteDto {
  title: string;
  content: string;
  category?: NoteCategory;
  priority?: NotePriority;
  birdId?: string;
  speciesId?: string;
  tags?: string[];
  metadata?: NoteMetadata;
  isPinned?: boolean;
  isArchived?: boolean;
  reminderDate?: string;
}

export interface UpdateNoteDto extends Partial<CreateNoteDto> {}

export interface GetNotesDto {
  page?: number;
  limit?: number;
  search?: string;
  birdId?: string;
  speciesId?: string;
  category?: NoteCategory;
  priority?: NotePriority;
  isPinned?: boolean;
  isArchived?: boolean;
  hasReminder?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  startDate?: string;
  endDate?: string;
}

export interface NotesResponse {
  success: boolean;
  data: Note[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface NoteStats {
  total: number;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
  pinned: number;
  archived: number;
  withReminders: number;
  recentNotes: number;
}

export interface CategoryOption {
  value: NoteCategory;
  label: string;
}

export interface PriorityOption {
  value: NotePriority;
  label: string;
}

// API functions
export const notesApi = {
  async getNotes(filters: GetNotesDto = {}): Promise<Note[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v.toString()));
        } else {
          params.set(key, value.toString());
        }
      }
    });

    const response = await api.get<any>(`notes?${params.toString()}`);
    // The controller returns `{ success, data: Note[], total, ... }`.
    return unwrap<Note[]>(response.data) || [];
  },

  async getNote(id: string): Promise<Note> {
    const response = await api.get<any>(`notes/${id}`);
    return unwrap<Note>(response.data);
  },

  async createNote(data: CreateNoteDto): Promise<Note> {
    // Clean up undefined values and empty arrays
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => 
        value !== undefined && 
        value !== 'undefined' && 
        value !== null &&
        !(Array.isArray(value) && value.length === 0)
      )
    );
    
    const response = await api.post<any>('notes', cleanData);
    return unwrap<Note>(response.data);
  },

  async updateNote(id: string, data: UpdateNoteDto): Promise<Note> {
    const response = await api.patch<any>(`notes/${id}`, data);
    return unwrap<Note>(response.data);
  },

  async deleteNote(id: string): Promise<void> {
    await api.delete<void>(`notes/${id}`);
  },

  async getStatistics(): Promise<NoteStats> {
    const response = await api.get<any>('notes/statistics');
    return unwrap<NoteStats>(response.data);
  },

  async getNotesByBird(birdId: string): Promise<Note[]> {
    const response = await api.get<any>(`notes/by-bird/${birdId}`);
    return unwrap<Note[]>(response.data) || [];
  },

  async getNotesBySpecies(speciesId: string): Promise<Note[]> {
    const response = await api.get<any>(`notes/by-species/${speciesId}`);
    return unwrap<Note[]>(response.data) || [];
  },

  async getNotesByCategory(category: NoteCategory): Promise<Note[]> {
    const response = await api.get<any>(`notes/by-category/${category}`);
    return unwrap<Note[]>(response.data) || [];
  },

  // Toggles the pinned state (server endpoint flips the current value).
  async togglePin(id: string): Promise<Note> {
    const response = await api.post<any>(`notes/${id}/pin`);
    return unwrap<Note>(response.data);
  },

  // Toggles the archived state (server endpoint flips the current value).
  async toggleArchive(id: string): Promise<Note> {
    const response = await api.post<any>(`notes/${id}/archive`);
    return unwrap<Note>(response.data);
  },

  async getCategoryOptions(): Promise<CategoryOption[]> {
    const response = await api.get<any>('notes/categories');
    return unwrap<CategoryOption[]>(response.data) || [];
  },

  async getPriorityOptions(): Promise<PriorityOption[]> {
    const response = await api.get<any>('notes/priorities');
    return unwrap<PriorityOption[]>(response.data) || [];
  }
};

// Helper functions
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const getPriorityColor = (priority: NotePriority): string => {
  switch (priority) {
    case NotePriority.LOW:
      return 'text-green-600 bg-green-100';
    case NotePriority.MEDIUM:
      return 'text-blue-600 bg-blue-100';
    case NotePriority.HIGH:
      return 'text-orange-600 bg-orange-100';
    case NotePriority.URGENT:
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

export const getCategoryColor = (category: NoteCategory): string => {
  switch (category) {
    case NoteCategory.HEALTH:
      return 'text-red-600 bg-red-100';
    case NoteCategory.BREEDING:
      return 'text-pink-600 bg-pink-100';
    case NoteCategory.FEEDING:
      return 'text-green-600 bg-green-100';
    case NoteCategory.BEHAVIOR:
      return 'text-purple-600 bg-purple-100';
    case NoteCategory.TRAINING:
      return 'text-indigo-600 bg-indigo-100';
    case NoteCategory.VETERINARY:
      return 'text-red-600 bg-red-100';
    case NoteCategory.REMINDER:
      return 'text-yellow-600 bg-yellow-100';
    case NoteCategory.OBSERVATION:
      return 'text-blue-600 bg-blue-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}; 